import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarkExpertNotificationAsViewedMutation } from 'shared/hooks/api/markExpertNotificationAsViewed/markExpertNotificationAsViewed.generated';
import { useMarkManyExpertNotificationsAsViewedMutation } from 'shared/hooks/api/markManyExpertNotificationAsViewed/markManyExpertNotificationsAsViewed.generated';
import { useNotificationsQuery } from 'shared/hooks/api/notifications/notifications.generated';
import { useNotificationStore } from 'shared/hooks/stores/useNotificationStore';
import { useSitemap } from 'shared/hooks/useSitemap';
import { NotificationTypeEnum } from 'types/enums/notification-type.enum';
import { useShallow } from 'zustand/react/shallow';

const jobOrderApplyNotifications: NotificationTypeEnum[] = [
  NotificationTypeEnum.AirConversationNotFinished,
  NotificationTypeEnum.JobOrderApplicationPreferencesStepNotFinished,
];
const profileNotifications: NotificationTypeEnum[] = [
  NotificationTypeEnum.CvExtractionFailed,
  NotificationTypeEnum.IncompleteProfile,
  NotificationTypeEnum.ProfileUpdateReminder,
];

export const useNotifications = () => {
  const navigate = useNavigate();
  const sitemap = useSitemap();
  const {
    notifications,
    markNotificationAsViewed,
    markManyNotificationsAsViewed,
    loadNotifications,
  } = useNotificationStore(
    useShallow((state) => ({
      notifications: state.notifications,
      markNotificationAsViewed: state.markNotificationAsViewed,
      markManyNotificationsAsViewed: state.markManyNotificationsAsViewed,
      loadNotifications: state.loadNotifications,
    })),
  );
  const [markNotificationAsViewedMutation, { loading: markAsViewedLoading }] =
    useMarkExpertNotificationAsViewedMutation();
  const [
    markManyExpertNotificationsAsViewedMutation,
    { loading: markManyExpertNotificationsAsViewedLoading },
  ] = useMarkManyExpertNotificationsAsViewedMutation();
  const { loading, refetch: refetchNotifications } = useNotificationsQuery({
    skip: !!notifications.length,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: ({ expertNotifications }) =>
      loadNotifications(
        expertNotifications.notifications.filter(({ type }) =>
          Object.values(NotificationTypeEnum).includes(type as unknown as NotificationTypeEnum),
        ),
      ),
  });

  const redirectToRelatedAndMarkAsViewed = useCallback(
    (notificationId: string) => {
      const notification = useNotificationStore
        .getState()
        .notifications.find((notif) => notif.id === notificationId);
      if (!notification) {
        return;
      }
      if (!notification.viewedAt) {
        markNotificationAsViewed(notificationId, new Date());
        markNotificationAsViewedMutation({
          variables: {
            notificationId,
          },
        })
          .then((res) => {
            if (res.data?.markExpertNotificationAsViewed.viewedAt) {
              markNotificationAsViewed(
                notificationId,
                res.data.markExpertNotificationAsViewed.viewedAt,
              );
            }
          })
          .catch(() => undefined);
      }

      if (profileNotifications.includes(notification.type)) {
        navigate(sitemap.profile());
        return;
      }

      if (notification.jobOrderId) {
        navigate(
          jobOrderApplyNotifications.includes(notification.type)
            ? sitemap.jobOrderApply(notification.jobOrderId)
            : sitemap.jobOrder(notification.jobOrderId),
        );
      }
    },
    [sitemap],
  );

  const markAsViewedByType = useCallback(async (type: NotificationTypeEnum) => {
    const notificationIdsToMark = useNotificationStore
      .getState()
      .notifications.filter((notif) => notif.type === type && !notif.viewedAt)
      .map(({ id }) => id);
    if (!notificationIdsToMark.length) {
      return;
    }

    const res = await markManyExpertNotificationsAsViewedMutation({
      variables: {
        notificationIds: notificationIdsToMark,
      },
    }).catch(() => undefined);

    if (res?.data?.markManyExpertNotificationsAsViewed) {
      markManyNotificationsAsViewed(res.data.markManyExpertNotificationsAsViewed);
    }
  }, []);

  return {
    notifications,
    redirectToRelatedAndMarkAsViewed,
    markAsViewedByType,
    loading,
    markAsViewedLoading,
    markManyExpertNotificationsAsViewedLoading,
    refetchNotifications,
  };
};
